import {isPassportIdentity} from '@sollapay/domain';
import {PartyType} from '@sollapay/enums';
import {FieldDatePicker, FieldInput, Icon, PhoneNumberField} from '@sollapay/ui/components';
import {Controller, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {FullNameField} from '@/components';
import {
  IdentityDocumentFields,
  identityFieldsCellClassName,
  identityNameCellClassName
} from '@/components/identity';
import {ControlledAddressField} from '@/features/address';
import {BuyerKycDocumentUpload} from '@/features/buyers/components';
import {useRefreshBuyerDetail} from '@/features/buyers/hooks';
import {
  generateIsraeliAddress,
  generatePhoneNumber,
  withDebugFieldChange,
  withDebugSetValue
} from '@/features/debug-test-data';
import {
  emailRule,
  fullNameRule,
  futureDateRule,
  idCountryRule,
  individualIdRule,
  phoneRule
} from '@/lib/validation';

import {useIdentityLock} from './useIdentityLock';

import type {EditBuyerFormValues} from './useEditBuyerForm';
import type {FieldLock} from './useIdentityLock';
import type {DebugSetValue} from '@/features/debug-test-data';
import type {FC} from 'react';
import type {UseFormReturn} from 'react-hook-form';

export interface EditPersonBuyerFieldsProps {
  form: UseFormReturn<EditBuyerFormValues>;
  trustAccountId: string;
  buyerId: string;
  isIdentityLocked: boolean;
  identityLockedTooltip?: string;
  addressLock: FieldLock;
}

export const EditPersonBuyerFields: FC<EditPersonBuyerFieldsProps> = ({
  form,
  trustAccountId,
  buyerId,
  isIdentityLocked,
  identityLockedTooltip,
  addressLock
}) => {
  const {t} = useTranslation('trustAccounts');
  const {
    register,
    control,
    formState: {errors}
  } = form;
  const refreshBuyer = useRefreshBuyerDetail(trustAccountId, buyerId);
  const idType = useWatch({control, name: 'idType'});
  const isPassport = isPassportIdentity(idType);
  const prefix = 'view.buyers.addBuyerModal.buyerDetails';
  const setDebugValue = form.setValue as DebugSetValue;
  const {identityLock, whenEditable} = useIdentityLock(isIdentityLocked, identityLockedTooltip);
  return (
    <div className="flex flex-col gap-6">
      <BuyerKycDocumentUpload
        partyType={PartyType.PERSON}
        idType={idType}
        trustAccountId={trustAccountId}
        metadata={{partyId: buyerId}}
        skipProcessing
        onDocumentChange={refreshBuyer}
      />

      <div className="grid grid-cols-2 gap-6">
        <div className={identityNameCellClassName(isPassport)}>
          <FullNameField
            label={t(`${prefix}.name`)}
            error={errors.fullName?.message}
            required
            {...identityLock}
            {...register('fullName', whenEditable(fullNameRule(t)))}
          />
        </div>
        <div className={identityFieldsCellClassName(isPassport)}>
          <IdentityDocumentFields
            control={control}
            isPassport={isPassport}
            idNumberName="nationalId"
            idCountryName="idCountry"
            idNumberRules={whenEditable(individualIdRule(t, isPassport))}
            idCountryRules={whenEditable(idCountryRule(t))}
            idNumberError={errors.nationalId?.message}
            idCountryError={errors.idCountry?.message}
            {...identityLock}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <Controller
          name="dateOfBirth"
          control={control}
          rules={whenEditable({
            required: t('common:fields.dateOfBirth.required'),
            ...futureDateRule(t)
          })}
          render={({field}) => (
            <FieldDatePicker
              label={t(`${prefix}.dateOfBirth`)}
              placeholder={t(`${prefix}.dateOfBirthPlaceholder`)}
              error={errors.dateOfBirth?.message}
              required
              disableFuture
              {...identityLock}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
        <FieldInput
          label={t(`${prefix}.email`)}
          placeholder={t(`${prefix}.emailPlaceholder`)}
          error={errors.email?.message}
          type="email"
          required
          startAdornment={<Icon name="mail" className="size-4" />}
          {...register('email', emailRule(t))}
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <Controller
          name="phone"
          control={control}
          rules={phoneRule(t)}
          render={({field}) => (
            <PhoneNumberField
              label={t(`${prefix}.phone`)}
              placeholder={t(`${prefix}.phonePlaceholder`)}
              error={errors.phone?.message}
              required
              cornerBadge={withDebugFieldChange(undefined, field.onChange, generatePhoneNumber)}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          )}
        />
        <ControlledAddressField
          control={control}
          name="address"
          label={t(`${prefix}.address`)}
          error={errors.address?.message}
          validateRequired={!addressLock.isLocked}
          {...addressLock}
          cornerBadge={withDebugSetValue(
            undefined,
            setDebugValue,
            'address',
            generateIsraeliAddress
          )}
        />
      </div>
    </div>
  );
};
